import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAI, GoogleAIBackend } from 'firebase/ai';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import { firebaseConfig, appCheckConfig } from '@config/app.config';

// Must be set BEFORE initializeApp so Firebase picks it up on first load.
// In dev the browser console will print the debug token — register it once in
// Firebase console → App Check → datalogichub → ⋮ → Manage debug tokens.
if (import.meta.env.DEV) {
  // @ts-ignore
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Firebase App Check
// - DEV:  debug token flow (no reCAPTCHA key needed; token printed to console)
// - PROD: reCAPTCHA v3 using VITE_RECAPTCHA_SITE_KEY
const recaptchaKey = appCheckConfig.recaptchaSiteKey || 'debug-placeholder';
initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(recaptchaKey),
  isTokenAutoRefreshEnabled: true,
});

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
// Explicitly pass the bucket URL — required for newer *.firebasestorage.app buckets
export const storage = getStorage(app, `gs://${firebaseConfig.storageBucket}`);

// Firebase AI Logic — uses Gemini Developer API backend managed by Firebase
// The Gemini API key is stored and managed by Firebase; never put it in client code.
export const firebaseAI = getAI(app, { backend: new GoogleAIBackend() });

// Google Analytics — only initialize when measurementId is present and the
// environment supports it (no SSR, no restricted browsers).
// The .env template ships "G-XXXXXXXXXX"; treat that placeholder as unset so
// gtag never boots against a bogus property.
const gaId = firebaseConfig.measurementId;
export let analytics = null;
if (gaId && !/^G-X+$/i.test(gaId)) {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

// Fire a GA4 event; no-ops until a real VITE_GA_MEASUREMENT_ID is configured,
// so call sites never need to guard.
export const trackEvent = (name, params = {}) => {
  if (analytics) {
    logEvent(analytics, name, params);
  }
};

export default app;
